import { useCallback, useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from './api';
import type { Status } from './api';

/**
 * Load-on-mount with the three states every screen here needs: loading, an error the
 * server wrote, and the data. Deliberately not a data-fetching library — there is no
 * caching or invalidation worth having in a console where each page is one request.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(describe(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(run, [run]);

  return { data, error, loading, reload: run, setError };
}

/**
 * Runs a mutation and reports on it: disables while in flight, surfaces the server's
 * message on failure. Every action button on a page shares one of these, so a page
 * cannot end up with two buttons both claiming to be busy.
 */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (cause) {
      setError(describe(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}

export function describe(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'Something went wrong';
}

/* ------------------------------------------------------------------ button */

/**
 * Buttons go through a component rather than element styles, because there are four
 * distinct looks and the difference between them carries meaning: primary is the one
 * thing a screen is for, danger asks before it acts, link is a control that should
 * not draw the eye at all.
 */
type Variant = 'primary' | 'ghost' | 'danger' | 'link';

const BUTTON_BASE =
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap ' +
  'transition-colors disabled:cursor-default disabled:opacity-50';

const BUTTON_VARIANT: Record<Variant, string> = {
  primary: 'rounded-lg bg-accent px-4 py-2 font-semibold text-white hover:bg-blue-400',
  ghost: 'rounded-lg bg-sunken px-4 py-2 font-semibold text-text hover:bg-hover',
  danger: 'rounded-lg border border-danger px-3 py-1.5 font-medium text-red-300 hover:bg-danger/10',
  link: 'font-medium text-accent hover:underline',
};

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${className}`.trim()}
    />
  );
}

/* -------------------------------------------------------------------- bits */

const PILL_TONE = {
  neutral: 'bg-sunken text-muted',
  on: 'bg-success/15 text-emerald-300',
  off: 'bg-slate-400/15 text-muted',
  warn: 'bg-warning/15 text-amber-300',
};

export function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: keyof typeof PILL_TONE;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PILL_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

export function StatusPill({ status }: { status: Status }) {
  if (status === 'active') return null;
  return <Pill tone="warn">Archived</Pill>;
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mb-3.5 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2.5 text-red-300">
      {message}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="py-4.5 text-muted">{children}</div>;
}

/** Tables must be able to scroll sideways on their own; the page must not. */
export function TableScroll({ children }: { children: ReactNode }) {
  return <div className="w-full overflow-x-auto">{children}</div>;
}

/** A column of controls with one gap between them, used inside cards and dialogs. */
export function Stack({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`flex flex-col gap-3 ${className}`.trim()}>{children}</div>;
}

/**
 * Auto-fit rather than fixed columns: one field fills the width, four sit in a row,
 * and no field needs a breakpoint of its own.
 */
export function FormGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-start gap-3.5">
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-xs text-muted">{label}</label>
      {children}
      {hint && <p className="mt-1.5 mb-0 text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function Card({
  title,
  action,
  children,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mb-4 rounded-xl border border-divider bg-raised p-3.5 sm:p-[18px]">
      {(title || action) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          {title ? <h2 className="text-[17px] font-semibold">{title}</h2> : <span />}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------- modal */

/**
 * A modal built on the native <dialog>.
 *
 * Native rather than a hand-rolled overlay because showModal() already gives focus
 * trapping, Escape to dismiss, inertness for the page behind it and a ::backdrop —
 * all of which are easy to get subtly wrong by hand, and all of which someone
 * operating this by keyboard will notice.
 *
 * Used for the short focused tasks: creating a thing, searching, or reading a secret
 * that is shown once. Per-row confirmation stays inline, where it can sit beside the
 * row it is about.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  /*
   * The same sync as the onClose prop below, wired natively.
   *
   * close does not bubble, so it is the one event React cannot reach by delegating
   * from the root, and it is the event everything here depends on: if it is missed,
   * state still believes the dialog is open, and the next click on the button that
   * opens it sets a state that is already set — no render, no showModal, a control
   * that silently does nothing. Two listeners for one event is cheap next to that.
   */
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const sync = () => onClose();
    dialog.addEventListener('close', sync);
    return () => dialog.removeEventListener('close', sync);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      // m-auto because preflight zeroes the margin a dialog is centred by; text-left
      // because a dialog opened from a table's right-aligned action cell would inherit it.
      className="m-auto w-full max-w-[min(560px,calc(100vw-2rem))] bg-transparent p-0 text-left text-text backdrop:bg-[rgba(2,6,16,0.66)]"
      // Escape closes the dialog directly, so React state has to be told about it
      // or the next open() would be a no-op against an already-open element.
      onClose={onClose}
      onClick={(event) => {
        // A click landing on the dialog itself is a click on the backdrop; the
        // panel inside stops its own clicks from reaching here.
        if (event.target === ref.current) onClose();
      }}
    >
      {/* dvh, so a phone's address bar cannot sit over the buttons at the bottom. */}
      <div
        className="max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-xl border border-divider bg-raised p-4 sm:p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-[17px] font-semibold">{title}</h2>
          <Button variant="link" onClick={onClose} aria-label="Close">
            Close
          </Button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

/** Actions sit right, where the eye lands after reading the form. */
export function ModalActions({ children }: { children: ReactNode }) {
  return (
    <div className="mt-1 flex justify-end gap-2.5 max-sm:[&>button]:w-full">{children}</div>
  );
}

/* -------------------------------------------------------------------- menu */

/**
 * A dropdown anchored to its own button.
 *
 * Not a <dialog>: a menu of two or three links should not take over the screen, and it
 * has to stay visually attached to the control that opened it. Light dismiss is done by
 * hand because that is the only part the platform does not give us here — pointerdown
 * outside, or Escape.
 */
export function Menu({
  className = '',
  buttonClassName = '',
  label,
  ariaLabel,
  align = 'start',
  children,
}: {
  className?: string;
  buttonClassName?: string;
  label: ReactNode;
  ariaLabel?: string;
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={`relative flex min-w-0 ${className}`.trim()} ref={wrap}>
      <button
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {label}
      </button>

      {open && (
        // Any click inside has done what it came to do — followed a link, signed out —
        // so the panel closes on the way out rather than each item saying so itself.
        <div
          role="menu"
          onClick={() => setOpen(false)}
          className={`absolute top-[calc(100%+8px)] z-30 min-w-[210px] max-w-[min(320px,calc(100vw-1.5rem))] rounded-[10px] border border-divider bg-raised p-1.5 shadow-[0_14px_34px_rgba(2,6,16,0.55)] ${
            align === 'end' ? 'right-0' : 'left-0'
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export const MENU_ITEM =
  'block w-full cursor-pointer rounded-md px-2.5 py-2.5 text-left font-medium ' +
  'whitespace-nowrap text-text no-underline hover:bg-hover';

/* -------------------------------------------------------------------- list */

/**
 * One row per thing, rather than a card each: a list of outlets is a list, and stacking
 * cards makes five outlets look like five separate topics.
 */
export function List({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-divider bg-raised">
      {children}
    </div>
  );
}

export function ListRow({
  title,
  subtitle,
  actions,
  archived = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  archived?: boolean;
}) {
  return (
    <div
      className={`flex gap-3.5 border-b border-divider px-4 py-3.5 last:border-b-0 hover:bg-hover max-sm:flex-wrap max-sm:items-start sm:items-center ${
        archived ? 'opacity-60' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5 font-semibold">{title}</div>
        {subtitle && <div className="mt-0.5 text-[12.5px] text-muted">{subtitle}</div>}
      </div>
      {actions && (
        // An Archive button rides on the row; the sentence it turns into when it asks
        // takes a line of its own rather than squeezing the name out of the row.
        <div className="flex flex-wrap items-center gap-2 max-sm:has-[.confirming]:basis-full">
          {actions}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- page header */

/** Where you are in organization > outlet > counters, and the way back up. */
export function Breadcrumbs({ trail }: { trail: { label: string; to?: string }[] }) {
  return (
    <nav className="mb-2.5 text-[13px] text-muted" aria-label="Breadcrumb">
      {trail.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`}>
          {index > 0 && <span className="mx-2 text-divider">/</span>}
          {crumb.to ? (
            <Link to={crumb.to} className="text-accent no-underline hover:underline">
              {crumb.label}
            </Link>
          ) : (
            <span className="text-text">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHead({
  title,
  hint,
  actions,
}: {
  title: string;
  hint?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5">
      <PageHeadRow actions={actions}>
        <h1 className="text-[19px] font-semibold sm:text-[22px]">{title}</h1>
      </PageHeadRow>
      {hint && <p className="mt-1.5 mb-0 max-w-[70ch] text-muted">{hint}</p>}
    </div>
  );
}

/** The same row without the heading, for a tab that only needs a hint and controls. */
export function PageHeadRow({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-4">
      {children}
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}

/* ----------------------------------------------------------------- actions */

/**
 * A destructive action that asks first, inline.
 *
 * Deliberately not window.confirm: that dialog cannot say what is about to happen in
 * the words of this app, cannot be styled to match it, and blocks the page while it
 * is open. Asking in place also keeps the consequence next to the thing it affects.
 */
export function ConfirmButton({
  label,
  question,
  detail,
  confirmLabel,
  busy,
  variant = 'danger',
  onConfirm,
}: {
  label: string;
  question: string;
  detail?: string;
  confirmLabel?: string;
  busy: boolean;
  variant?: Variant;
  onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <Button variant={variant} disabled={busy} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }

  return (
    // The marker class is what lets a list row give the question its own line on a
    // phone without giving one to the small button it replaced.
    <span className="confirming inline-flex flex-wrap items-center gap-2.5 rounded-lg border border-danger/35 bg-danger/8 px-2.5 py-1.5">
      <span className="max-w-[46ch] text-[12.5px]">
        {question}
        {detail && <span className="mt-0.5 block text-muted">{detail}</span>}
      </span>
      <Button
        variant="danger"
        disabled={busy}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        {confirmLabel ?? 'Yes, archive'}
      </Button>
      <Button variant="link" onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </span>
  );
}

/**
 * Archive and restore in one control.
 *
 * Archiving asks first, because it takes children with it — an organization carries
 * its outlets and counters, and an outlet carries its counters. Restoring does not
 * ask: it is the undo, and a confirm in front of undo only makes people hesitate over
 * the safe direction.
 */
export function ArchiveButton({
  status,
  label,
  cascadeNote,
  busy,
  onArchive,
  onRestore,
}: {
  status: Status;
  label: string;
  cascadeNote?: string;
  busy: boolean;
  onArchive: () => void;
  onRestore: () => void;
}) {
  if (status === 'archived') {
    return (
      <Button variant="ghost" disabled={busy} onClick={onRestore}>
        Restore
      </Button>
    );
  }

  return (
    <ConfirmButton
      label="Archive"
      question={`Archive ${label}?`}
      detail={cascadeNote}
      confirmLabel="Yes, archive"
      busy={busy}
      onConfirm={onArchive}
    />
  );
}

/** "Show archived" — off by default, so a list reads as what is in use today. */
export function ArchivedToggle({
  showArchived,
  onChange,
}: {
  showArchived: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="m-0 inline-flex items-center gap-[7px] text-[13px] text-muted">
      <input
        type="checkbox"
        checked={showArchived}
        onChange={(event) => onChange(event.target.checked)}
      />
      Show archived
    </label>
  );
}

/**
 * A value you can correct in place without a form around it.
 *
 * Used for the MPOS ids, which nothing reads yet — they belong on the row as
 * something to fill in when the receipt join needs them, not as a question asked
 * while someone is trying to create a counter.
 */
export function InlineEdit({
  label,
  value,
  placeholder,
  hint,
  busy,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  hint?: string;
  busy: boolean;
  onSave: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const row = 'flex flex-wrap items-center gap-2.5 py-1.5';
  const name = 'text-xs text-muted max-sm:min-w-full sm:min-w-[118px]';

  if (!editing) {
    return (
      <div className={row}>
        <span className={name}>{label}</span>
        <span className={value ? 'font-mono text-xs break-all' : 'text-muted'}>
          {value || 'not set'}
        </span>
        <Button
          variant="link"
          onClick={() => {
            setDraft(value ?? '');
            setEditing(true);
          }}
        >
          {value ? 'Change' : 'Set'}
        </Button>
      </div>
    );
  }

  return (
    <div className={row}>
      <span className={name}>{label}</span>
      <input
        autoFocus
        className="w-auto flex-[1_1_160px]"
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onSave(draft.trim());
            setEditing(false);
          }
          if (event.key === 'Escape') setEditing(false);
        }}
      />
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => {
          onSave(draft.trim());
          setEditing(false);
        }}
      >
        Save
      </Button>
      <Button variant="link" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {hint && <p className="mt-1.5 mb-0 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/**
 * A value the server will only ever show once — a pairing code, an API key. Rendered so
 * it reads as something to copy now rather than something to come back for.
 *
 * `large` is for a value read off this screen and typed in by hand at a till - the pairing
 * code - so it is sized to be read from across a counter. A key is copied, never typed,
 * and is too long to set large.
 */
export function ShowOnce({
  label,
  value,
  note,
  large = false,
}: {
  label: string;
  value: string;
  note?: string;
  large?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mt-3 rounded-lg border border-accent/45 bg-accent/10 p-3">
      <label className="mb-1 block text-xs text-muted">{label}</label>
      {/* A large code gets a line of its own, Copy beneath: beside the button a phone
          wrapped it at the dash, which is the one place it must not break. */}
      <div className={large ? 'flex flex-col items-center gap-2' : 'flex flex-wrap items-center gap-3'}>
        <code
          className={
            large
              ? 'py-2 text-center font-mono text-[34px] leading-tight font-semibold tracking-[0.12em] whitespace-nowrap text-text sm:text-[44px]'
              : 'flex-1 font-mono text-xs break-all'
          }
        >
          {value}
        </code>
        <Button
          variant="ghost"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-2 mb-0 text-muted">
        {note ?? 'Shown once. It cannot be retrieved after you leave this page.'}
      </p>
    </div>
  );
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}
